import React from 'react';
import { Group, Circle, Rect, Line, RegularPolygon, Star, Path } from 'react-konva';
import { AlignmentGuidesOverlay } from '../overlays/AlignmentGuidesOverlay';
import { ConnectionPreview } from '../overlays/ConnectionPreview';
import { ConnectionTriggers } from '../overlays/ConnectionTriggers';
import { DistributionGuidesOverlay } from '../overlays/DistributionGuidesOverlay';
import { RotationHotspots } from '../overlays/RotationHotspots';
import { Node } from '../../../core/models/scene';
import { SelectionOverlay } from '../../diagram/SelectionOverlay';
import * as awarenessProtocol from 'y-protocols/awareness';
import { KonvaShapeRenderer, getShapeProperties } from '../../../lib/genogram/konva-shape-renderer';
import { LinkIndicator } from '../overlays/LinkIndicator';
import { useHistoryPreviewStore } from '../../../stores/historyPreviewStore';
import { useOrgChartViewStore } from '../../../stores/orgchartViewStore';
import type { AlignmentLine } from '../../../domains/diagram/models/AlignmentGuides';
import type { DistributionGapSegment } from '../../../domains/diagram/models/DistributionGuides';

const ORG_NODE_TYPES = new Set([
  'org_person',
  'org_person_photo',
  'org_person_photo_left',
  'org_person_photo_right',
  'org_person_photo_inner_left',
  'org_person_text',
  'org_position',
  'org_department'
]);

interface OverlaysLayerProps {
  // Connection preview
  connectionPreview: {
    startX: number;
    startY: number;
    endX: number;
    endY: number;
    visible: boolean;
  };
  previewStartAnchor: 'top' | 'right' | 'bottom' | 'left' | null;
  previewEndAnchor: 'top' | 'right' | 'bottom' | 'left' | null;
  firstConnectNode: string | null;
  hoveredTargetId: string | null;
  nodes: Node[];
  
  // Connection triggers
  selectedIds: Set<string>;
  transformerRect?: { x: number; y: number; width: number; height: number } | null;
  onConnectionStart: (side: 'top'|'right'|'bottom'|'left', startX: number, startY: number) => void;
  onQuickCreate: (side: 'top'|'right'|'bottom'|'left', screenX: number, screenY: number) => void;
  
  // Rotation hotspots
  onRotationStart: (startAngle: number) => void;
  // Lock actions
  onUnlockNode?: (nodeId: string) => void;

  // Visibility flags
  suppressConnectionTriggers?: boolean;
  
  // Hover target visualization
  hoveredTargetPoint: { x: number; y: number } | null;
  hoveredTargetAnchor: 'top' | 'right' | 'bottom' | 'left' | null;
  hoveredTargetAnchors: Array<{ id: string; side: 'top'|'right'|'bottom'|'left'; pt: { x:number; y:number } }>;
  isConnecting: boolean;
  
  // Previews
  penPreview: {
    points: number[];
    color: string;
    width: number;
    opacity: number;
    visible: boolean;
  };
  placePreview: {
    x: number; 
    y: number; 
    width?: number; 
    height?: number; 
    visible: boolean; 
    shapeId?: string; 
    category?: string;
    fillColor?: string;
    strokeColor?: string;
    fromPoint?: { x: number; y: number };
    toPoint?: { x: number; y: number };
  };
  
  // Selection marquee
  selectionRect: {
    x: number;
    y: number;
    width: number;
    height: number;
    visible: boolean;
  };
  // Collaboration overlays
  awareness?: awarenessProtocol.Awareness | null;
  currentUserId?: string;
  overlayShapes?: Map<string, { x: number; y: number; width: number; height: number }>;
  // Permissions
  canWrite?: boolean;
  // Optional: hover indicator for marriage bar during connect
  marriageHover?: { x1: number; x2: number; y: number } | null;

  // Alignment guides (optional; rendered during drag/transform/rotate)
  alignmentGuides?: AlignmentLine[];
  alignmentViewportRect?: { left: number; top: number; right: number; bottom: number } | null;

  // Distribution/equal-spacing guides (optional; rendered during drag/rotate)
  distributionSegments?: DistributionGapSegment[];
}

type PlacePreviewData = OverlaysLayerProps['placePreview'];

const renderLinePlacementPreview = (placePreview: PlacePreviewData): React.ReactNode => {
  if (placePreview.shapeId !== 'line' || !placePreview.fromPoint || !placePreview.toPoint) return null;
  return (
    <Line
      points={[
        placePreview.fromPoint.x,
        placePreview.fromPoint.y,
        placePreview.toPoint.x,
        placePreview.toPoint.y
      ]}
      stroke="#334155"
      strokeWidth={2}
      opacity={0.9}
      listening={false}
      lineCap="round"
    />
  );
};

const renderGenogramPlacementPreview = (
  placePreview: PlacePreviewData,
  id: string,
  w: number,
  h: number,
): React.ReactNode => {
  if (placePreview.category !== 'genogram') return null;
  const size = Math.min(w, h);
  const shapeProps = getShapeProperties(id);
  return (
    <Group
      x={placePreview.x}
      y={placePreview.y}
      opacity={0.9}
      listening={false}
    >
      <KonvaShapeRenderer
        shapeType={id}
        size={size}
        fillColor="#f5f5f5"
        strokeColor="#333"
        strokeWidth={2}
        {...shapeProps}
      />
    </Group>
  );
};

const renderOrgPlacementPreview = (
  placePreview: PlacePreviewData,
  id: string,
  w: number,
  h: number,
): React.ReactNode => {
  const isOrgShape = placePreview.category === 'org' || id.startsWith('org_');
  if (!isOrgShape) return null;

  const safeW = Math.max(8, w);
  const safeH = Math.max(8, h);
  const pad = Math.max(2, Math.min(safeW, safeH) * 0.06);
  const stroke = '#334155';
  const fill = '#ffffff';
  const personStroke = '#10B981';
  const personFill = '#FFFFFF';
  const personAccent = '#D1FAE5';
  const personAccentSoft = '#ECFDF5';
  const opacity = 0.9;

  if (id === 'org_position' || id === 'org_department') {
    const cardX = pad;
    const cardY = pad;
    const cardWidth = Math.max(4, safeW - (pad * 2));
    const cardHeight = Math.max(4, safeH - (pad * 2));
    return (
      <Group x={placePreview.x} y={placePreview.y} listening={false} opacity={opacity}>
        <Rect
          x={cardX}
          y={cardY}
          width={cardWidth}
          height={cardHeight}
          cornerRadius={4}
          fill={fill}
          stroke={stroke}
          strokeWidth={2}
        />
      </Group>
    );
  }

  if (
    id === 'org_person' ||
    id === 'org_person_photo' ||
    id === 'org_person_photo_left' ||
    id === 'org_person_photo_right' ||
    id === 'org_person_photo_inner_left' ||
    id === 'org_person_text'
  ) {
    const cardX = pad;
    const cardY = pad;
    const cardWidth = Math.max(4, safeW - (pad * 2));
    const cardHeight = Math.max(4, safeH - (pad * 2));
    const line1Y = cardY + Math.max(3, cardHeight * 0.28);
    const line2Y = cardY + Math.max(7, cardHeight * 0.52);
    return (
      <Group x={placePreview.x} y={placePreview.y} listening={false} opacity={opacity}>
        <Rect
          x={cardX}
          y={cardY}
          width={cardWidth}
          height={cardHeight}
          cornerRadius={4}
          fill={personFill}
          stroke={personStroke}
          strokeWidth={2}
        />
        <Rect
          x={cardX + pad}
          y={line1Y}
          width={Math.max(6, cardWidth * 0.58)}
          height={Math.max(2, cardHeight * 0.12)}
          cornerRadius={2}
          fill={personAccent}
        />
        <Rect
          x={cardX + pad}
          y={line2Y}
          width={Math.max(5, cardWidth * 0.42)}
          height={Math.max(2, cardHeight * 0.1)}
          cornerRadius={2}
          fill={personAccentSoft}
        />
      </Group>
    );
  }

  return null;
};

const renderDefaultPlacementPreview = (
  placePreview: PlacePreviewData,
  id: string,
  w: number,
  h: number,
): React.ReactNode => {
  if (id === 'circle' || id === 'female') {
    const r = Math.min(w, h) / 2;
    return (
      <Circle
        x={placePreview.x + r}
        y={placePreview.y + r}
        radius={r}
        stroke="#334155"
        fill="#ffffff"
        opacity={0.9}
        listening={false}
      />
    );
  }

  if (id === 'terminator') {
    const r = Math.min(w, h) / 2;
    return (
      <Circle
        x={placePreview.x + r}
        y={placePreview.y + r}
        radius={r}
        stroke="#334155"
        fill="#ffffff"
        opacity={0.9}
        listening={false}
      />
    );
  }

  if (id === 'triangle') {
    return (
      <Line
        x={placePreview.x}
        y={placePreview.y}
        points={[
          w / 2, 0,
          w, h,
          0, h,
        ]}
        closed
        stroke="#334155"
        fill="#ffffff"
        opacity={0.9}
        listening={false}
      />
    );
  }

  if (id === 'decision') {
    return (
      <Line
        x={placePreview.x}
        y={placePreview.y}
        points={[
          w / 2, 0,
          w, h / 2,
          w / 2, h,
          0, h / 2,
        ]}
        closed
        stroke="#334155"
        fill="#ffffff"
        opacity={0.9}
        listening={false}
      />
    );
  }

  if (id === 'data') {
    const cut = Math.min(w * 0.3, h * 0.4);
    return (
      <Line
        x={placePreview.x}
        y={placePreview.y}
        points={[
          cut, 0,
          w, 0,
          w - cut, h,
          0, h,
        ]}
        closed
        stroke="#334155"
        fill="#ffffff"
        opacity={0.9}
        listening={false}
      />
    );
  }

  if (id === 'diamond' || id === 'unknown') {
    return (
      <Line
        x={placePreview.x}
        y={placePreview.y}
        points={[
          w / 2, 0,
          w, h / 2,
          w / 2, h,
          0, h / 2,
        ]}
        closed
        stroke="#334155"
        fill="#ffffff"
        opacity={0.9}
        listening={false}
      />
    );
  }

  if (id === 'document') {
    const wave = Math.min(h * 0.3, 24);
    const pathData = [
      `M0 0`,
      `L${w} 0`,
      `L${w} ${h - wave}`,
      `Q${w * 0.75} ${h} ${w * 0.5} ${h - wave / 2}`,
      `Q${w * 0.25} ${h - wave} 0 ${h}`,
      `Z`,
    ].join(' ');
    return (
      <Path
        x={placePreview.x}
        y={placePreview.y}
        data={pathData}
        stroke="#334155"
        fill="#ffffff"
        opacity={0.9}
        listening={false}
      />
    );
  }

  if (id === 'hexagon') {
    return (
      <RegularPolygon
        x={placePreview.x + w / 2}
        y={placePreview.y + h / 2}
        sides={6}
        radius={Math.min(w, h) / 2}
        stroke="#334155"
        fill="#ffffff"
        opacity={0.9}
        listening={false}
      />
    );
  }

  if (id === 'pentagon') {
    return (
      <RegularPolygon
        x={placePreview.x + w / 2}
        y={placePreview.y + h / 2}
        sides={5}
        radius={Math.min(w, h) / 2}
        stroke="#334155"
        fill="#ffffff"
        opacity={0.9}
        listening={false}
      />
    );
  }

  if (id === 'star') {
    const size = Math.min(w, h);
    return (
      <Star
        x={placePreview.x + size / 2}
        y={placePreview.y + size / 2}
        numPoints={5}
        innerRadius={size / 4}
        outerRadius={size / 2}
        stroke="#334155"
        fill="#ffffff"
        opacity={0.9}
        listening={false}
      />
    );
  }

  const isSticky = placePreview.category === 'sticky-note' || id === 'sticky-note';
  return (
    <Rect
      x={placePreview.x}
      y={placePreview.y}
      width={w}
      height={h}
      fill={isSticky ? (placePreview.fillColor || '#FEF3C7') : '#ffffff'}
      stroke={isSticky ? (placePreview.strokeColor || '#FCD34D') : '#334155'}
      opacity={0.9}
      listening={false}
      cornerRadius={isSticky ? 4 : 0}
    />
  );
};

const renderPlacementPreviewByCategory = (placePreview: PlacePreviewData): React.ReactNode => {
  const w = placePreview.width || 60;
  const h = placePreview.height || 40;
  const id = placePreview.shapeId || 'rectangle';

  const linePreview = renderLinePlacementPreview(placePreview);
  if (linePreview) return linePreview;

  const orgPreview = renderOrgPlacementPreview(placePreview, id, w, h);
  if (orgPreview) return orgPreview;

  const genogramPreview = renderGenogramPlacementPreview(placePreview, id, w, h);
  if (genogramPreview) return genogramPreview;

  return renderDefaultPlacementPreview(placePreview, id, w, h);
};

export const OverlaysLayer: React.FC<OverlaysLayerProps> = ({
  connectionPreview,
  previewStartAnchor,
  previewEndAnchor,
  firstConnectNode,
  hoveredTargetId,
  nodes,
  selectedIds,
  transformerRect,
  onConnectionStart,
  onQuickCreate,
  onRotationStart,
  onUnlockNode,
  suppressConnectionTriggers,
  hoveredTargetPoint,
  hoveredTargetAnchor,
  hoveredTargetAnchors,
  isConnecting,
  penPreview,
  placePreview,
  selectionRect,
  awareness,
  currentUserId,
  overlayShapes,
  canWrite = true,
  marriageHover,
  alignmentGuides = [],
  alignmentViewportRect = null,
  distributionSegments = []
}) => {
  const selectedId = selectedIds.size === 1 ? Array.from(selectedIds)[0] : null;
  const selectedNode = selectedId ? nodes.find(n => n.id === selectedId) : null;
  const isOrgNode = selectedNode ? ORG_NODE_TYPES.has(selectedNode.type) : false;
  const orgAutoLayoutEnabled = useOrgChartViewStore((s) => s.autoLayoutEnabled);
  const diffHighlights = useHistoryPreviewStore((s) => s.diffHighlights);
  
  return (
    <>
      {/* Connection Preview */}
      {connectionPreview.visible && (
        <ConnectionPreview
          start={{ x: connectionPreview.startX, y: connectionPreview.startY }}
          end={{ x: connectionPreview.endX, y: connectionPreview.endY }}
          startSide={previewStartAnchor || 'right'}
          endSide={previewEndAnchor || 'left'}
          startRotation={(((nodes.find(n => n.id === firstConnectNode)?.attrs as any)?.rotation) as number) || 0}
          endRotation={(((nodes.find(n => n.id === hoveredTargetId!)?.attrs as any)?.rotation) as number) || 0}
          snapped={!!hoveredTargetId}
          color="#3b82f6"
        />
      )}

      {/* Marriage bar hover indicator while connecting */}
      {isConnecting && marriageHover && (
        <Line
          points={[marriageHover.x1, marriageHover.y, marriageHover.x2, marriageHover.y]}
          stroke="#3b82f6"
          strokeWidth={14}
          opacity={0.25}
          listening={false}
        />
      )}

      {/* Pen live preview */}
      {penPreview.visible && penPreview.points.length >= 2 && (
        <Line
          points={penPreview.points}
          stroke={penPreview.color}
          strokeWidth={penPreview.width}
          opacity={penPreview.opacity}
          lineCap="round"
          lineJoin="round"
          tension={0.5}
          listening={false}
          perfectDrawEnabled={false}
        />
      )}
      
      {/* Shape placement preview */}
      {placePreview.visible && renderPlacementPreviewByCategory(placePreview)}

      <AlignmentGuidesOverlay guides={alignmentGuides} viewportRect={alignmentViewportRect} />
      <DistributionGuidesOverlay segments={distributionSegments} />
      
      {/* Rotation hotspots for single selection (hidden when node is locked or viewer mode) */}
      {selectedIds.size === 1 && selectedNode && canWrite && !((selectedNode as any)?.locked || (selectedNode as any)?.attrs?.locked) && (() => {
        const w = selectedNode.w || selectedNode.width || 60;
        const h = selectedNode.h || selectedNode.height || 60;
        const cx = selectedNode.x + (w / 2);
        const cy = selectedNode.y + (h / 2);
        const rotation = ((selectedNode.attrs as any)?.rotation) || 0;
        
        return (
          <RotationHotspots
            cx={cx}
            cy={cy}
            width={w}
            height={h}
            rotation={rotation}
            onStart={onRotationStart}
          />
        );
      })()}

      {/* Lock overlay for locked selections. User-owned locks are clickable to
          unlock; projection-owned locks (synthesized scenes rebuilt from
          canonical records) render nothing — the lock is structural, an unlock
          affordance would silently revert on the next rebuild, and the
          live-view banner names the state instead. */}
      {(() => {
        const overlays: React.ReactNode[] = [];
        selectedIds.forEach(id => {
          const n = nodes.find(nn => nn.id === id) as any;
          if (!n) return;
          const isLocked = !!(n.locked || n?.attrs?.locked);
          if (!isLocked) return;
          if (n.lockOwner === 'projection') return;
          const w = n.w || n.width || 60;
          const h = n.h || n.height || 60;
          let cx = n.x + (w / 2);
          let cy = n.y + (h / 2); // center of shape
          // If single selection and transformer rect exists, use it for stable centering
          if (selectedIds.size === 1 && transformerRect && id === Array.from(selectedIds)[0]) {
            if (transformerRect.width > 0 && transformerRect.height > 0) {
              cx = transformerRect.x + transformerRect.width / 2;
              cy = transformerRect.y + transformerRect.height / 2;
            }
          }
          overlays.push(
            <Group
              key={`lock-${id}`}
              name="overlay-lock"
              listening={true}
              perfectDrawEnabled={false}
              onClick={(e) => { e.cancelBubble = true; onUnlockNode?.(id); }}
            >
              <Circle
                x={cx}
                y={cy}
                radius={8}
                fill="#FFFFFF"
                stroke="#CBD5E1"
                listening={true}
                perfectDrawEnabled={false}
                shadowForStrokeEnabled={false}
                hitStrokeWidth={0}
                strokeScaleEnabled={false}
              />
              {/* Lucide lock icon (24x24 viewbox scaled smaller) */}
              <Group
                x={cx - (12 * 0.6)}
                y={cy - (12 * 0.6)}
                scaleX={0.6}
                scaleY={0.6}
                listening={false}
                perfectDrawEnabled={false}
              >
                <Rect
                  x={3}
                  y={11}
                  width={18}
                  height={11}
                  cornerRadius={2}
                  stroke="#0F172A"
                  strokeWidth={2}
                  listening={false}
                  perfectDrawEnabled={false}
                  shadowForStrokeEnabled={false}
                  hitStrokeWidth={0}
                  strokeScaleEnabled={false}
                />
                <Path
                  data="M7 11 V7 a5 5 0 0 1 10 0 v4"
                  stroke="#0F172A"
                  strokeWidth={2}
                  listening={false}
                  perfectDrawEnabled={false}
                  shadowForStrokeEnabled={false}
                  hitStrokeWidth={0}
                  strokeScaleEnabled={false}
                />
              </Group>
            </Group>
          );
        });
        return overlays;
      })()}
      
      {/* Link indicators for shapes with linked diagrams */}
      {nodes.map((node) => {
        const attrs = (node.attrs as Record<string, unknown>) ?? {};
        const linkedDiagramId = attrs.linkedDiagramId as string | undefined;
        const linkId = attrs.linkId as string | undefined;
        
        if (!linkedDiagramId || !linkId) return null;
        
        // Position indicator at top-right corner of shape
        const width = (node.width ?? node.w ?? 60) as number;
        const indicatorX = node.x + width - 12; // Offset from right edge
        const indicatorY = node.y + 12; // Offset from top edge
        
        return (
          <LinkIndicator
            key={`link-${node.id}`}
            x={indicatorX}
            y={indicatorY}
            onClick={(e) => {
              e.cancelBubble = true;
              // Dispatch a custom event that the canvas can listen to
              if (typeof window !== 'undefined') {
                window.dispatchEvent(new CustomEvent('canvas:link:click', {
                  detail: { nodeId: node.id, linkedDiagramId, linkId },
                }));
              }
            }}
          />
        );
      })}
      
      {/* Connection triggers positioned from Transformer bounds */}
      {selectedIds.size === 1 && transformerRect && !suppressConnectionTriggers && (
        <ConnectionTriggers
          rect={transformerRect}
          enabledSides={isOrgNode && orgAutoLayoutEnabled ? ['top', 'bottom'] : undefined}
          onStart={onConnectionStart}
          onQuickCreate={onQuickCreate}
        />
      )}
      
      {/* Hover target anchor highlight while connecting or dragging endpoint */}
      {isConnecting && hoveredTargetPoint && hoveredTargetAnchor && (
        <Group listening={false}>
          <Circle x={hoveredTargetPoint.x} y={hoveredTargetPoint.y} radius={8} fill="#3b82f6" opacity={0.2} />
          <Circle x={hoveredTargetPoint.x} y={hoveredTargetPoint.y} radius={5} fill="#3b82f6" stroke="#ffffff" strokeWidth={2} />
        </Group>
      )}
      
      {isConnecting && hoveredTargetAnchors.length > 0 && (
        <Group listening={false}>
          {hoveredTargetAnchors.map((a, idx) => (
            <Circle key={`near-${idx}`} x={a.pt.x} y={a.pt.y} radius={4} fill="#3b82f6" opacity={0.35} />
          ))}
        </Group>
      )}
      
      {/* Selection marquee */}
      {selectionRect.visible && (
        <Rect
          x={selectionRect.x}
          y={selectionRect.y}
          width={selectionRect.width}
          height={selectionRect.height}
          fill="rgba(33, 150, 243, 0.1)"
          stroke="rgba(33, 150, 243, 0.5)"
          strokeWidth={1}
          listening={false}
        />
      )}

      {/* Remote selection overlays for shapes (collaboration) */}
      {awareness && nodes.length > 0 && (
        <Group listening={false}>
          {nodes.map((n) => {
            const shape = overlayShapes?.get(n.id);
            const w = shape?.width ?? n.w ?? n.width ?? 60;
            const h = shape?.height ?? n.h ?? n.height ?? 60;
            const x = shape?.x ?? n.x;
            const y = shape?.y ?? n.y;
            return (
              <SelectionOverlay
                key={`sel-${n.id}`}
                shapeId={n.id}
                x={x}
                y={y}
                width={w}
                height={h}
                awareness={awareness}
                currentUserId={currentUserId}
              />
            );
          })}
        </Group>
      )}

      {/* Version diff highlights */}
      {diffHighlights.length > 0 && (
        <Group listening={false}>
          {diffHighlights.map((h) => {
            const color =
              h.type === 'deleted' ? '#16a34a' :
              h.type === 'added' ? '#dc2626' :
              '#facc15';
            return (
              <Rect
                key={`diff-${h.id}`}
                x={h.x - 4}
                y={h.y - 4}
                width={h.width + 8}
                height={h.height + 8}
                stroke={color}
                strokeWidth={2}
                dash={[6, 4]}
                listening={false}
                cornerRadius={3}
              />
            );
          })}
        </Group>
      )}

    </>
  );
};
